import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import config from 'config';
import {
  CLEAR_USER_PREFERENCES,
  DELETE_USER_PROFILE_REQUEST,
  FETCH_USER_PROFILES_REQUEST,
  logOut,
  UPDATE_USER_PROFILE_REQUEST,
} from 'actions';
import { UserProfile } from '../../../../decls/bom/userProfile';

type Props = {
  user: { email?: string; username?: string };
  dispatch: Function;
  userProfiles?: UserProfile[];
  userProfilesLoading?: boolean;
  profileSaving?: boolean;
};

type State = {
  open: boolean;
  managingProfiles: boolean;
  editingProfileId: number | null;
  editingProfileTitle: string;
  selectedProfileId: number | null;
  selectedProfileTitle: string;
  deletingProfileId: number | null;
};

class V3AvatarMenu extends React.Component<Props, State> {
  private wrapRef: React.RefObject<HTMLDivElement>;

  constructor(props: Props) {
    super(props);
    this.state = {
      open: false,
      managingProfiles: false,
      editingProfileId: null,
      editingProfileTitle: '',
      selectedProfileId: null,
      selectedProfileTitle: '',
      deletingProfileId: null,
    };
    this.wrapRef = React.createRef();
  }

  componentDidMount() {
    document.addEventListener('click', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  }

  componentDidUpdate(prevProps: Props) {
    if (
      this.state.managingProfiles &&
      prevProps.profileSaving &&
      !this.props.profileSaving &&
      this.state.editingProfileId !== null
    ) {
      this.cancelEditingProfile();
    }
  }

  handleClickOutside = (e: MouseEvent) => {
    if (
      this.state.open &&
      this.wrapRef.current &&
      !this.wrapRef.current.contains(e.target as Node)
    ) {
      this.setState({ open: false });
    }
  };

  toggle = () => {
    this.setState((prev) => ({ open: !prev.open }));
  };

  openProfileManager = () => {
    const { dispatch, userProfilesLoading } = this.props;
    this.setState({
      open: false,
      managingProfiles: true,
      editingProfileId: null,
      editingProfileTitle: '',
      selectedProfileId: null,
      selectedProfileTitle: '',
      deletingProfileId: null,
    });
    if (!userProfilesLoading) {
      dispatch(FETCH_USER_PROFILES_REQUEST());
    }
  };

  closeProfileManager = () => {
    this.setState({
      managingProfiles: false,
      editingProfileId: null,
      editingProfileTitle: '',
      deletingProfileId: null,
    });
  };

  openProfileDetails = (profile: UserProfile) => {
    if (typeof profile.id !== 'number') return;
    this.setState({
      managingProfiles: false,
      editingProfileId: null,
      editingProfileTitle: '',
      selectedProfileId: profile.id,
      selectedProfileTitle: profile.title || '',
    });
  };

  closeProfileDetails = () => {
    this.setState({
      managingProfiles: true,
      selectedProfileId: null,
      selectedProfileTitle: '',
    });
  };

  handleSelectedProfileTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ selectedProfileTitle: e.target.value });
  };

  selectedProfile = (): UserProfile | null => {
    const { selectedProfileId } = this.state;
    if (selectedProfileId === null) return null;
    const profiles = Array.isArray(this.props.userProfiles) ? this.props.userProfiles : [];
    return profiles.find((p) => p && p.id === selectedProfileId) || null;
  };

  buildProfileTags = (profile: UserProfile): string[] => {
    const cfg = profile && profile.project_config ? profile.project_config : {};
    const pc = cfg.project_configuration || {};
    const advanced = cfg.advanced_settings || {};
    const system = cfg.system || {};
    const module = cfg.module || {};
    const tags: string[] = [];
    if (system.code) tags.push(system.code);
    else if (system.label) tags.push(system.label);
    if (module.mfg_name) tags.push(module.mfg_name);
    if (module.model_name) tags.push(module.model_name);
    const railDirection = pc.rail_direction || advanced.rail_direction;
    const preferredSpan = pc.preferred_span || advanced.preferred_span;
    const rafterSpacing = pc.rafter_spacing_inches || advanced.rafter_spacing_inches;
    if (railDirection) tags.push(railDirection);
    if (preferredSpan) tags.push(`Span: ${preferredSpan}"`);
    if (rafterSpacing) tags.push(`Rafter: ${rafterSpacing}"`);
    return tags.filter(Boolean);
  };

  saveSelectedProfile = () => {
    const { dispatch } = this.props;
    const profile = this.selectedProfile();
    const title = this.state.selectedProfileTitle.trim();
    if (!profile || typeof profile.id !== 'number' || !title) return;
    dispatch(UPDATE_USER_PROFILE_REQUEST({
      profileId: profile.id,
      profile: { ...profile, title },
    }));
    this.closeProfileDetails();
  };

  startEditingProfile = (profile: UserProfile) => {
    if (typeof profile.id !== 'number') return;
    this.setState({
      editingProfileId: profile.id,
      editingProfileTitle: profile.title || '',
    });
  };

  cancelEditingProfile = () => {
    this.setState({
      editingProfileId: null,
      editingProfileTitle: '',
    });
  };

  handleEditingProfileTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ editingProfileTitle: e.target.value });
  };

  saveEditingProfile = () => {
    const { dispatch, userProfiles } = this.props;
    const { editingProfileId, editingProfileTitle } = this.state;
    if (editingProfileId === null) return;
    const trimmedTitle = editingProfileTitle.trim();
    if (!trimmedTitle) return;
    const profile = (userProfiles || []).find((p) => p && p.id === editingProfileId);
    if (!profile) return;
    dispatch(UPDATE_USER_PROFILE_REQUEST({
      profileId: editingProfileId,
      profile: { ...profile, title: trimmedTitle },
    }));
  };

  requestDeleteProfile = (profile: UserProfile) => {
    if (typeof profile.id !== 'number') return;
    this.setState({ deletingProfileId: profile.id });
  };

  cancelDeleteProfile = () => {
    this.setState({ deletingProfileId: null });
  };

  profilePendingDelete = (): UserProfile | null => {
    const { deletingProfileId } = this.state;
    if (deletingProfileId === null) return null;
    const profiles = Array.isArray(this.props.userProfiles) ? this.props.userProfiles : [];
    return profiles.find((p) => p && p.id === deletingProfileId) || null;
  };

  confirmDeleteProfile = () => {
    const { dispatch } = this.props;
    const profile = this.profilePendingDelete();
    if (!profile || typeof profile.id !== 'number') return;
    dispatch(DELETE_USER_PROFILE_REQUEST({ profileId: profile.id }));
    this.setState({ deletingProfileId: null });
  };

  handleLogout = () => {
    const { dispatch } = this.props;
    dispatch(CLEAR_USER_PREFERENCES());
    dispatch(logOut());
    window.location.href = config.ssoLogoutUrl;
  };

  renderManageProfilesButton() {
    return (
      <button type="button" className="v3-avatar-item" onClick={this.openProfileManager}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path
            d="M2 2h8l2 2v8H2z"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinejoin="round"
          />
          <path
            d="M4 2v4h6V2M4 8h6"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
          />
        </svg>
        Manage Saved Profiles
      </button>
    );
  }

  renderProfilesManager() {
    const { userProfiles, userProfilesLoading, profileSaving } = this.props;
    const { editingProfileId, editingProfileTitle } = this.state;
    const profiles = Array.isArray(userProfiles) ? userProfiles : [];
    return (
      <div className="v3-save-prf-overlay">
        <div className="v3-save-prf-card v3-profile-manager-card">
          <div className="v3-save-prf-hdr">
            <div className="v3-save-prf-title">Manage Saved Profiles</div>
            <button
              type="button"
              className="v3-modal-close"
              onClick={this.closeProfileManager}
              aria-label="Close saved profiles manager"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            </button>
          </div>
          <div className="v3-save-prf-body">
            <p className="v3-save-prf-desc">
              Saved profiles you created are listed below.
            </p>
            <div className="v3-profile-manager-list">
              {userProfilesLoading && profiles.length === 0 && (
                <div className="v3-profile-manager-empty">Loading profiles...</div>
              )}
              {!userProfilesLoading && profiles.length === 0 && (
                <div className="v3-profile-manager-empty">No saved profiles yet.</div>
              )}
              {profiles.map((profile) => {
                const profileId = typeof profile.id === 'number' ? profile.id : null;
                const isEditing = profileId !== null && editingProfileId === profileId;
                const saveDisabled = !editingProfileTitle.trim() || !!profileSaving;
                return (
                  <div key={`${profileId || 'profile'}-${profile.title}`} className="v3-profile-manager-row">
                    {isEditing ? (
                      <React.Fragment>
                        <input
                          className="v3-profile-manager-input"
                          value={editingProfileTitle}
                          onChange={this.handleEditingProfileTitleChange}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !saveDisabled) this.saveEditingProfile();
                            if (e.key === 'Escape') this.cancelEditingProfile();
                          }}
                          autoFocus
                        />
                        <div className="v3-profile-manager-actions">
                          <button
                            type="button"
                            className="v3-profile-manager-action primary"
                            onClick={this.saveEditingProfile}
                            disabled={saveDisabled}
                          >
                            {profileSaving ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            type="button"
                            className="v3-profile-manager-action"
                            onClick={this.cancelEditingProfile}
                            disabled={!!profileSaving}
                          >
                            Cancel
                          </button>
                        </div>
                      </React.Fragment>
                    ) : (
                      <React.Fragment>
                        <div className="v3-profile-manager-name" title={profile.title}>
                          {profile.title || 'Untitled profile'}
                        </div>
                        <div className="v3-profile-manager-actions">
                          <button
                            type="button"
                            className="v3-profile-manager-action primary"
                            onClick={() => this.openProfileDetails(profile)}
                            disabled={profileId === null || !!profileSaving}
                          >
                            Update
                          </button>
                          <button
                            type="button"
                            className="v3-profile-manager-action danger"
                            onClick={() => this.requestDeleteProfile(profile)}
                            disabled={profileId === null || !!profileSaving}
                          >
                            Delete
                          </button>
                        </div>
                      </React.Fragment>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="v3-save-prf-ftr">
            <button
              type="button"
              className="v3-btn v3-btn-primary"
              onClick={this.closeProfileManager}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  renderDeleteProfileModal() {
    const profile = this.profilePendingDelete();
    if (!profile) return null;
    const title = profile.title || 'Untitled profile';
    return (
      <div className="v3-save-prf-overlay" onClick={this.cancelDeleteProfile}>
        <div className="v3-save-prf-card" onClick={(e) => e.stopPropagation()}>
          <div className="v3-save-prf-hdr">
            <div className="v3-save-prf-title">Delete Saved Profile</div>
            <button
              type="button"
              className="v3-modal-close"
              onClick={this.cancelDeleteProfile}
              aria-label="Close delete saved profile dialog"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            </button>
          </div>
          <div className="v3-save-prf-body">
            <p className="v3-save-prf-desc">
              This saved profile will be permanently deleted.
            </p>
            <div className="v3-save-prf-preview">
              <span className="v3-save-prf-tag">{title}</span>
            </div>
          </div>
          <div className="v3-save-prf-ftr">
            <button
              type="button"
              className="v3-btn v3-btn-ghost"
              onClick={this.cancelDeleteProfile}
            >
              Cancel
            </button>
            <button
              type="button"
              className="v3-btn v3-btn-orange"
              onClick={this.confirmDeleteProfile}
              disabled={!!this.props.profileSaving}
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  }

  renderProfileDetailsModal() {
    const profile = this.selectedProfile();
    if (!profile) return null;
    const tags = this.buildProfileTags(profile);
    const disabled = !this.state.selectedProfileTitle.trim() || !!this.props.profileSaving;
    return (
      <div className="v3-save-prf-overlay" onClick={this.closeProfileDetails}>
        <div className="v3-save-prf-card" onClick={(e) => e.stopPropagation()}>
          <div className="v3-save-prf-hdr">
            <div className="v3-save-prf-title">Save as Profile</div>
            <button
              type="button"
              className="v3-modal-close"
              onClick={this.closeProfileDetails}
              aria-label="Close saved profile details"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            </button>
          </div>
          <div className="v3-save-prf-body">
            <p className="v3-save-prf-desc">
              This profile will save your system config so you can reuse it when creating new projects.
            </p>
            {tags.length > 0 && (
              <div className="v3-save-prf-preview">
                {tags.map((t, i) => (
                  <span key={`${t}-${i}`} className="v3-save-prf-tag">{t}</span>
                ))}
              </div>
            )}
            <div className="v3-modal-field" style={{ marginBottom: 0 }}>
              <label>Profile Name</label>
              <input
                className="v3-modal-input"
                type="text"
                placeholder="e.g. Standard Residential NXT"
                value={this.state.selectedProfileTitle}
                onChange={this.handleSelectedProfileTitleChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !disabled) this.saveSelectedProfile();
                  if (e.key === 'Escape') this.closeProfileDetails();
                }}
                autoFocus
              />
            </div>
          </div>
          <div className="v3-save-prf-ftr">
            <button
              type="button"
              className="v3-btn v3-btn-ghost"
              onClick={this.closeProfileDetails}
            >
              Cancel
            </button>
            <button
              type="button"
              className="v3-btn v3-btn-primary"
              onClick={this.saveSelectedProfile}
              disabled={disabled}
            >
              {this.props.profileSaving ? 'Saving...' : 'Save Profile'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { user } = this.props;
    const { open, managingProfiles } = this.state;

    return (
      <div className="v3-avatar-wrap" ref={this.wrapRef}>
        <button type="button" className="v3-avatar-circle" onClick={this.toggle}>
          <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
            <circle cx="8.5" cy="6" r="3" stroke="#fff" strokeWidth="1.4" />
            <path
              d="M2 15c0-3.5 3-5.5 6.5-5.5S15 11.5 15 15"
              stroke="#fff"
              strokeWidth="1.4"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <div className={`v3-avatar-menu${open ? ' open' : ''}`}>
          <div>
            <div className="v3-avatar-email">
              <div className="v3-avatar-email-lbl">Signed in as</div>
              <div className="v3-avatar-email-val">
                {user.email || user.username || 'User'}
              </div>
            </div>

            {this.renderManageProfilesButton()}

            <div className="v3-avatar-sep" />

            <button type="button" className="v3-avatar-item danger" onClick={this.handleLogout}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M9 2h3v10H9M6 10l4-3-4-3M1 7h7"
                  stroke="currentColor"
                  strokeWidth="1.3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Logout
            </button>
          </div>
        </div>
        {managingProfiles && ReactDOM.createPortal(this.renderProfilesManager(), document.body)}
        {this.state.selectedProfileId !== null && ReactDOM.createPortal(this.renderProfileDetailsModal(), document.body)}
        {this.state.deletingProfileId !== null && ReactDOM.createPortal(this.renderDeleteProfileModal(), document.body)}
      </div>
    );
  }
}

function mapStateToProps(appState: appState) {
  const lite = appState.liteProject || {};
  return {
    userProfiles: Array.isArray(lite.userProfiles) ? lite.userProfiles : [],
    userProfilesLoading: !!lite.userProfilesLoading,
    profileSaving: !!lite.profileSaving,
  };
}

export default connect(mapStateToProps)(V3AvatarMenu);
